/** @jsx jsx */
import { jsx } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButton, IContextualMenuItem } from '@fluentui/react';
import { useContext, useMemo } from 'react';
import { SettingButtonsDisable } from '../../types/types';
import { ColumnOptionsContext } from './ColumnOptions';
import { ViewDialogState } from './ColumnOptionsPanel';
import { isColumnFiltered } from './utils';

type Props = {
  setViewDialog: (viewDialogState: ViewDialogState) => void;
  disableButtons?: SettingButtonsDisable;
};

export const ViewContextButton = ({ setViewDialog, disableButtons }: Props) => {
  const {
    selectedView,
    onClickDeleteView,
    onClickClearAllFilters,
    onClickReset,
    onClickSaveView,
    columns,
    strings,
    hideViewOptionsFeatures,
    disableNewView,
    id
  } = useContext(ColumnOptionsContext);

  const areAnyFiltersSelected = useMemo(() => {
    return columns.some(c => isColumnFiltered(c.filter));
  }, [columns]);

  const items: IContextualMenuItem[] = [
    {
      hidden: hideViewOptionsFeatures,
      key: 'new',
      id: id ? `${id}-options-new` : undefined,
      text: strings.newViewButtonLabel,
      iconProps: { iconName: 'Add', id: id ? `${id}-icon-add` : undefined },

      disabled: disableButtons && disableButtons['new'] ? disableButtons['new'] : false,
      onClick: () =>
        setViewDialog({
          isOpen: true,
          userAppSettingId: null
        })
    },
    {
      hidden: hideViewOptionsFeatures,
      key: 'save',
      id: id ? `${id}-options-save` : undefined,
      text: strings.saveViewButtonLabel,
      iconProps: { iconName: 'Save', id: id ? `${id}-icon-save` : undefined },
      disabled: disableButtons && disableButtons['save'] ? disableButtons['save'] : !selectedView,
      onClick: onClickSaveView
    },
    {
      hidden: hideViewOptionsFeatures,
      key: 'edit',
      id: id ? `${id}-options-edit` : undefined,
      text: strings.editViewButtonLabel,
      iconProps: { iconName: 'Edit', id: id ? `${id}-icon-edit` : undefined },
      disabled: disableButtons && disableButtons['edit'] ? disableButtons['edit'] : !selectedView,
      onClick: () => {
        if (!selectedView) return;
        setViewDialog({
          isOpen: true,
          userAppSettingId: selectedView.userAppSettingId
        });
      }
    },
    {
      hidden: false,
      key: 'reset',
      id: id ? `${id}-options-reset` : undefined,
      text: strings.resetButtonLabel,
      iconProps: { iconName: 'Undo', id: id ? `${id}-icon-undo` : undefined },
      disabled: disableButtons && disableButtons['reset'] ? disableButtons['reset'] : false,
      onClick: onClickReset
    },
    {
      hidden: false,
      key: 'clearAllFilters',
      id: id ? `${id}-options-clear` : undefined,
      text: strings.clearFiltersButtonLabel,
      iconProps: { iconName: 'ClearFilter', id: id ? `${id}-icon-clear` : undefined },
      disabled: disableButtons && disableButtons['clearAllFilters'] ? disableButtons['clearAllFilters'] : !areAnyFiltersSelected,
      onClick: onClickClearAllFilters
    },
    {
      hidden: hideViewOptionsFeatures,
      key: 'delete',
      id: id ? `${id}-options-delete` : undefined,
      text: strings.deleteViewButtonLabel,
      iconProps: { iconName: 'Delete', id: id ? `${id}-icon-delete` : undefined },
      disabled: disableButtons && disableButtons['delete'] ? disableButtons['delete'] : !selectedView,
      onClick: onClickDeleteView
    }
  ];

  return (
    <StyledIconButton
      disabled={typeof disableNewView === 'undefined' ? false : disableNewView}
      iconProps={{ iconName: 'MoreVertical' }}
      menuProps={{
        items: items.filter((item: any) => !item.hidden)
      }}
      menuIconProps={{ style: { display: 'none' } }}
      id={id ? `${id}-btn-dots` : undefined}
    />
  );
};

const StyledIconButton = styled(IconButton)`
  background: transparent;
  border: 1px solid transparent;
  &.is-expanded {
    border: 1px solid ${p => p.theme.palette.themePrimary};
  }
  &:hover {
    border: 1px solid ${p => p.theme.palette.themePrimary};
    background: transparent;
  }
`;
